// Internationalization translations - all UI text externalized
export const translations = {
  en: {
    // Navigation
    nav_home: "Home",
    nav_mission: "Mission",
    nav_explanation: "How It Works",
    nav_imprint: "Imprint",
    
    // Main Page
    app_title: "WordRiver",
    app_subtitle: "Real-time Speech-to-Text Transcription",
    
    // Controls
    btn_start: "Start",
    btn_pause: "Pause",
    btn_stop: "Stop",
    btn_resume: "Resume",
    btn_copy: "Copy Text",
    btn_export_txt: "Export as .txt",
    btn_export_rtf: "Export as .rtf",
    btn_clear: "Clear",
    
    // Settings
    settings_title: "Settings",
    settings_language: "Recognition Language",
    settings_ui_language: "Interface Language",
    settings_microphone: "Microphone",
    settings_font_size: "Text Size",
    settings_line_spacing: "Line Spacing",
    settings_text_fields: "Number of Text Fields",
    settings_theme: "Theme",
    settings_theme_light: "Light",
    settings_theme_dark: "Dark",
    
    // Status
    status_ready: "Ready to record",
    status_listening: "Listening...",
    status_paused: "Paused",
    status_stopped: "Stopped",
    status_not_supported: "Speech recognition not supported in this browser",
    status_permission_denied: "Microphone permission denied",
    status_permission_hint: "Click Start and allow microphone access when prompted",
    
    // Transcript
    transcript_placeholder: "Your transcribed text will appear here...",
    transcript_empty: "No transcript yet. Click 'Start' to begin recording.",
    
    // Toasts
    toast_copied: "Text copied to clipboard",
    toast_exported: "File exported successfully",
    toast_error: "An error occurred",
    toast_mic_changed: "Microphone changed",
    
    // Auto-save
    autosave_label: "Auto-saved",
    autosave_just_now: "Just now",
    
    // Keyboard shortcuts
    keyboard_shortcuts: "Keyboard Shortcuts",
    keyboard_space: "Space: Start/Pause",
    keyboard_escape: "Escape: Stop",
    keyboard_copy: "Ctrl+Shift+C: Copy",
    
    // Footer
    footer_support: "Support this project",
    footer_donate: "Donate",
    footer_privacy: "Privacy Policy",
    footer_copyright: "© 2025 WordRiver. All rights reserved.",
    
    // Mission Page
    mission_title: "Our Mission",
    mission_subtitle: "Making Communication Accessible to Everyone",
    mission_p1: "WordRiver was developed to break down communication barriers. We believe that everyone deserves equal access to spoken information, whether in the classroom, in meetings, or in everyday life.",
    mission_p2: "Our app offers real-time speech-to-text transcription and is designed to help people understand conversations, discussions, or presentations.",
    mission_p3: "By using free, browser-based technology, this tool is intended to be accessible to anyone with internet access.",
    
    // Explanation Page
    explanation_title: "How It Works",
    explanation_subtitle: "Simple, Free",
    explanation_step1_title: "1. Select Your Language",
    explanation_step1_desc: "Choose the language being spoken. Our app supports German, English, French, Italian, Spanish, and Japanese.",
    explanation_step2_title: "2. Choose Your Microphone",
    explanation_step2_desc: "Select which microphone to use. You can also adjust the text size and line spacing in the settings.",
    explanation_step3_title: "3. Start Recording",
    explanation_step3_desc: "Click 'Start' and speak. Your words will appear as text in real-time.",
    explanation_step4_title: "4. Export or Copy",
    explanation_step4_desc: "When finished, copy the text to your clipboard or export it as a .txt or .rtf file. Use the Clear button to delete the transcript and start fresh.",
    explanation_tech_title: "Technology",
    explanation_tech_desc: "We use the Web Speech API, a free browser technology, for speech recognition. This means no data is sent to external servers beyond what your browser requires.",
    
    // Browser Compatibility
    browser_compatibility_title: "Browser Compatibility",
    browser_compatibility_desc: "This application uses the Web Speech API for speech recognition. Please use one of the supported browsers:",
    browser_supported: "Supported",
    browser_not_supported: "Not supported",
    browser_recommendation: "Recommendation: For the best experience, we recommend using Google Chrome.",
    browser_check_supported: "Browser Compatible",
    browser_check_supported_desc: "Your browser ({browser}) supports speech recognition. You can use all features.",
    browser_check_not_supported: "Browser Not Compatible",
    browser_check_not_supported_desc: "Your browser does not support speech recognition.",
    browser_check_firefox_msg: "Firefox does not support the Web Speech API for speech recognition.",
    browser_check_recommendation: "Please use Chrome, Edge, Safari, or Opera instead.",
    
    // Imprint Page
    imprint_title: "Imprint",
    imprint_legal: "Legal Information",
    imprint_legal_text: "This is a free web application for real-time speech-to-text transcription. The application uses the Web Speech API provided by your browser.",
    imprint_contact: "Contact",
    imprint_contact_text: "Social Pedagogy at the University of Graz, Austria",
    imprint_disclaimer: "Disclaimer",
    imprint_disclaimer_text: "This application uses browser-based speech recognition technology. Accuracy may vary depending on audio quality, accent, and environmental factors. This tool is provided as-is without warranty.",
    imprint_important_notice: "Important Notice",
    imprint_privacy_data: "Data Processing",
    imprint_privacy_data_text: "All speech recognition is processed locally in your browser using the Web Speech API. We do not send your audio or transcripts to any external servers beyond what your browser requires for speech recognition.",
    imprint_privacy_storage: "Local Storage",
    imprint_privacy_storage_text: "Your settings (language preferences, theme, microphone selection) are stored locally in your browser and are not transmitted to any server.",
    imprint_privacy_donations: "Donations",
    imprint_privacy_donations_text: "If you choose to donate, you will be redirected to Stripe's secure payment page. We do not have access to your payment information.",
    
    // Languages
    lang_de: "German",
    lang_en: "English", 
    lang_fr: "French",
    lang_it: "Italian",
    lang_es: "Spanish",
    lang_ja: "Japanese",
  },
  
  de: {
    nav_home: "Startseite",
    nav_mission: "Mission",
    nav_explanation: "So funktioniert's",
    nav_imprint: "Impressum",
    
    app_title: "WordRiver",
    app_subtitle: "Echtzeit Sprache-zu-Text Transkription",
    
    btn_start: "Start",
    btn_pause: "Pause",
    btn_stop: "Stopp",
    btn_resume: "Fortsetzen",
    btn_copy: "Text kopieren",
    btn_export_txt: "Als .txt exportieren",
    btn_export_rtf: "Als .rtf exportieren",
    btn_clear: "Löschen",
    
    settings_title: "Einstellungen",
    settings_language: "Erkennungssprache",
    settings_ui_language: "Oberflächensprache",
    settings_microphone: "Mikrofon",
    settings_font_size: "Textgröße",
    settings_line_spacing: "Zeilenabstand",
    settings_text_fields: "Anzahl der Textfelder",
    settings_theme: "Design",
    settings_theme_light: "Hell",
    settings_theme_dark: "Dunkel",
    
    status_ready: "Bereit zur Aufnahme",
    status_listening: "Hört zu...",
    status_paused: "Pausiert",
    status_stopped: "Gestoppt",
    status_not_supported: "Spracherkennung wird in diesem Browser nicht unterstützt",
    status_permission_denied: "Mikrofonberechtigung verweigert",
    status_permission_hint: "Klicken Sie auf Start und erlauben Sie den Mikrofonzugriff",
    
    transcript_placeholder: "Ihr transkribierter Text erscheint hier...",
    transcript_empty: "Noch keine Transkription. Klicken Sie auf 'Start' um die Aufnahme zu beginnen.",
    
    toast_copied: "Text in die Zwischenablage kopiert",
    toast_exported: "Datei erfolgreich exportiert",
    toast_error: "Ein Fehler ist aufgetreten",
    toast_mic_changed: "Mikrofon geändert",
    
    // Auto-save
    autosave_label: "Automatisch gespeichert",
    autosave_just_now: "Gerade eben",
    
    // Keyboard shortcuts
    keyboard_shortcuts: "Tastenkürzel",
    keyboard_space: "Leertaste: Start/Pause",
    keyboard_escape: "Escape: Stopp",
    keyboard_copy: "Strg+Umschalt+C: Kopieren",
    
    footer_support: "Unterstützen Sie dieses Projekt",
    footer_donate: "Spenden",
    footer_privacy: "Datenschutz",
    footer_copyright: "© 2025 WordRiver. Alle Rechte vorbehalten.",
    
    mission_title: "Unsere Mission",
    mission_subtitle: "Kommunikation für alle zugänglich machen",
    mission_p1: "WordRiver wurde entwickelt, um Kommunikationsbarrieren abzubauen. Wir glauben, dass jeder gleichberechtigten Zugang zu gesprochenen Informationen verdient, ob im Unterricht, in Meetings oder im Alltag.",
    mission_p2: "Unsere App bietet Echtzeit-Sprache-zu-Text-Transkription und soll Menschen helfen, Gespräche, Diskussionen oder Präsentationen zu verstehen.",
    mission_p3: "Durch die Verwendung kostenloser, browserbasierter Technologie soll dieses Tool für jeden mit Internetzugang zugänglich sein.",
    
    explanation_title: "So funktioniert's",
    explanation_subtitle: "Einfach, Kostenlos",
    explanation_step1_title: "1. Sprache wählen",
    explanation_step1_desc: "Wählen Sie die gesprochene Sprache. Unsere App unterstützt Deutsch, Englisch, Französisch, Italienisch, Spanisch und Japanisch.",
    explanation_step2_title: "2. Mikrofon auswählen",
    explanation_step2_desc: "Wählen Sie das zu verwendende Mikrofon. In den Einstellungen können Sie auch die Textgröße und den Zeilenabstand anpassen.",
    explanation_step3_title: "3. Aufnahme starten",
    explanation_step3_desc: "Klicken Sie auf 'Start' und sprechen Sie. Ihre Worte erscheinen in Echtzeit als Text.",
    explanation_step4_title: "4. Exportieren oder Kopieren",
    explanation_step4_desc: "Wenn fertig, kopieren Sie den Text oder exportieren Sie ihn als .txt oder .rtf Datei. Mit der Löschen-Schaltfläche können Sie das Transkript löschen und neu beginnen.",
    explanation_tech_title: "Technologie",
    explanation_tech_desc: "Wir verwenden die Web Speech API, eine kostenlose Browser-Technologie. Das bedeutet, dass keine Daten an externe Server gesendet werden.",
    
    // Browser Compatibility
    browser_compatibility_title: "Browser-Kompatibilität",
    browser_compatibility_desc: "Diese Anwendung verwendet die Web Speech API für die Spracherkennung. Bitte verwenden Sie einen der unterstützten Browser:",
    browser_supported: "Unterstützt",
    browser_not_supported: "Nicht unterstützt",
    browser_recommendation: "Empfehlung: Für die beste Erfahrung empfehlen wir Google Chrome.",
    browser_check_supported: "Browser kompatibel",
    browser_check_supported_desc: "Ihr Browser ({browser}) unterstützt die Spracherkennung. Sie können alle Funktionen nutzen.",
    browser_check_not_supported: "Browser nicht kompatibel",
    browser_check_not_supported_desc: "Ihr Browser unterstützt keine Spracherkennung.",
    browser_check_firefox_msg: "Firefox unterstützt die Web Speech API für Spracherkennung nicht.",
    browser_check_recommendation: "Bitte verwenden Sie stattdessen Chrome, Edge, Safari oder Opera.",
    
    imprint_title: "Impressum",
    imprint_legal: "Rechtliche Informationen",
    imprint_legal_text: "Dies ist eine kostenlose Webanwendung für Echtzeit-Sprache-zu-Text-Transkription. Die Anwendung nutzt die Web Speech API Ihres Browsers.",
    imprint_contact: "Kontakt",
    imprint_contact_text: "Sozialpädagogik an der Universität Graz, Österreich",
    imprint_disclaimer: "Haftungsausschluss",
    imprint_disclaimer_text: "Diese Anwendung verwendet browserbasierte Spracherkennungstechnologie. Die Genauigkeit kann je nach Audioqualität, Akzent und Umgebungsfaktoren variieren. Dieses Tool wird ohne Gewährleistung bereitgestellt.",
    imprint_important_notice: "Wichtiger Hinweis",
    imprint_privacy_data: "Datenverarbeitung",
    imprint_privacy_data_text: "Die gesamte Spracherkennung wird lokal in Ihrem Browser mit der Web Speech API verarbeitet. Wir senden Ihre Audio- oder Transkriptionsdaten nicht an externe Server.",
    imprint_privacy_storage: "Lokale Speicherung",
    imprint_privacy_storage_text: "Ihre Einstellungen (Spracheinstellungen, Design, Mikrofonauswahl) werden lokal in Ihrem Browser gespeichert und nicht an Server übertragen.",
    imprint_privacy_donations: "Spenden",
    imprint_privacy_donations_text: "Wenn Sie spenden möchten, werden Sie zur sicheren Zahlungsseite von Stripe weitergeleitet. Wir haben keinen Zugriff auf Ihre Zahlungsinformationen.",
    
    lang_de: "Deutsch",
    lang_en: "Englisch",
    lang_fr: "Französisch",
    lang_it: "Italienisch",
    lang_es: "Spanisch",
    lang_ja: "Japanisch",
  },
  
  fr: {
    nav_home: "Accueil",
    nav_mission: "Mission",
    nav_explanation: "Comment ça marche",
    nav_imprint: "Mentions légales",
    
    app_title: "WordRiver",
    app_subtitle: "Transcription Parole-en-Texte en Temps Réel",
    
    btn_start: "Démarrer",
    btn_pause: "Pause",
    btn_stop: "Arrêter",
    btn_resume: "Reprendre",
    btn_copy: "Copier le texte",
    btn_export_txt: "Exporter en .txt",
    btn_export_rtf: "Exporter en .rtf",
    btn_clear: "Effacer",
    
    settings_title: "Paramètres",
    settings_language: "Langue de reconnaissance",
    settings_ui_language: "Langue de l'interface",
    settings_microphone: "Microphone",
    settings_font_size: "Taille du texte",
    settings_line_spacing: "Interligne",
    settings_text_fields: "Nombre de champs de texte",
    settings_theme: "Thème",
    settings_theme_light: "Clair",
    settings_theme_dark: "Sombre",
    
    status_ready: "Prêt à enregistrer",
    status_listening: "Écoute...",
    status_paused: "En pause",
    status_stopped: "Arrêté",
    status_not_supported: "La reconnaissance vocale n'est pas prise en charge par ce navigateur",
    status_permission_denied: "Permission du microphone refusée",
    status_permission_hint: "Cliquez sur Démarrer et autorisez l'accès au microphone",
    
    transcript_placeholder: "Votre texte transcrit apparaîtra ici...",
    transcript_empty: "Pas encore de transcription. Cliquez sur 'Démarrer' pour commencer.",
    
    toast_copied: "Texte copié dans le presse-papiers",
    toast_exported: "Fichier exporté avec succès",
    toast_error: "Une erreur s'est produite",
    toast_mic_changed: "Microphone changé",
    
    // Auto-save
    autosave_label: "Sauvegarde auto",
    autosave_just_now: "À l'instant",
    
    // Keyboard shortcuts
    keyboard_shortcuts: "Raccourcis clavier",
    keyboard_space: "Espace: Démarrer/Pause",
    keyboard_escape: "Échap: Arrêter",
    keyboard_copy: "Ctrl+Maj+C: Copier",
    
    footer_support: "Soutenez ce projet",
    footer_donate: "Faire un don",
    footer_privacy: "Politique de confidentialité",
    footer_copyright: "© 2025 WordRiver. Tous droits réservés.",
    
    mission_title: "Notre Mission",
    mission_subtitle: "Rendre la communication accessible à tous",
    mission_p1: "WordRiver a été développé pour briser les barrières de communication. Nous croyons que chacun mérite un accès égal à l'information parlée, que ce soit en classe, en réunion ou dans la vie quotidienne.",
    mission_p2: "Notre application offre une transcription parole-en-texte en temps réel et est conçue pour aider les gens à comprendre les conversations, les discussions ou les présentations.",
    mission_p3: "En utilisant une technologie gratuite basée sur le navigateur, cet outil est destiné à être accessible à toute personne disposant d'un accès Internet.",
    
    explanation_title: "Comment ça marche",
    explanation_subtitle: "Simple, Gratuit",
    explanation_step1_title: "1. Sélectionnez votre langue",
    explanation_step1_desc: "Choisissez la langue parlée. Notre app supporte l'allemand, l'anglais, le français, l'italien, l'espagnol et le japonais.",
    explanation_step2_title: "2. Choisissez votre microphone",
    explanation_step2_desc: "Sélectionnez le microphone à utiliser. Vous pouvez également ajuster la taille du texte et l'interligne dans les paramètres.",
    explanation_step3_title: "3. Commencez l'enregistrement",
    explanation_step3_desc: "Cliquez sur 'Démarrer' et parlez. Vos mots apparaîtront en texte en temps réel.",
    explanation_step4_title: "4. Exportez ou copiez",
    explanation_step4_desc: "Copiez le texte ou exportez-le en fichier .txt ou .rtf. Utilisez le bouton Effacer pour supprimer la transcription et recommencer.",
    explanation_tech_title: "Technologie",
    explanation_tech_desc: "Nous utilisons l'API Web Speech, une technologie de navigateur gratuite.",
    
    // Browser Compatibility
    browser_compatibility_title: "Compatibilité des navigateurs",
    browser_compatibility_desc: "Cette application utilise l'API Web Speech pour la reconnaissance vocale. Veuillez utiliser l'un des navigateurs pris en charge:",
    browser_supported: "Pris en charge",
    browser_not_supported: "Non pris en charge",
    browser_recommendation: "Recommandation: Pour une meilleure expérience, nous recommandons Google Chrome.",
    browser_check_supported: "Navigateur compatible",
    browser_check_supported_desc: "Votre navigateur ({browser}) prend en charge la reconnaissance vocale. Vous pouvez utiliser toutes les fonctionnalités.",
    browser_check_not_supported: "Navigateur non compatible",
    browser_check_not_supported_desc: "Votre navigateur ne prend pas en charge la reconnaissance vocale.",
    browser_check_firefox_msg: "Firefox ne prend pas en charge l'API Web Speech pour la reconnaissance vocale.",
    browser_check_recommendation: "Veuillez utiliser Chrome, Edge, Safari ou Opera à la place.",
    
    imprint_title: "Mentions légales",
    imprint_legal: "Informations légales",
    imprint_legal_text: "Ceci est une application web gratuite pour la transcription parole-en-texte en temps réel. L'application utilise l'API Web Speech fournie par votre navigateur.",
    imprint_contact: "Contact",
    imprint_contact_text: "Pédagogie sociale à l'Université de Graz, Autriche",
    imprint_disclaimer: "Avertissement",
    imprint_disclaimer_text: "Cette application utilise la technologie de reconnaissance vocale du navigateur. La précision peut varier en fonction de la qualité audio, de l'accent et des facteurs environnementaux. Cet outil est fourni tel quel sans garantie.",
    imprint_important_notice: "Avis important",
    imprint_privacy_data: "Traitement des données",
    imprint_privacy_data_text: "Toute la reconnaissance vocale est traitée localement dans votre navigateur à l'aide de l'API Web Speech. Nous n'envoyons pas vos données audio ou de transcription à des serveurs externes.",
    imprint_privacy_storage: "Stockage local",
    imprint_privacy_storage_text: "Vos paramètres (préférences de langue, thème, sélection du microphone) sont stockés localement dans votre navigateur et ne sont pas transmis à un serveur.",
    imprint_privacy_donations: "Dons",
    imprint_privacy_donations_text: "Si vous choisissez de faire un don, vous serez redirigé vers la page de paiement sécurisée de Stripe. Nous n'avons pas accès à vos informations de paiement.",
    
    lang_de: "Allemand",
    lang_en: "Anglais",
    lang_fr: "Français",
    lang_it: "Italien",
    lang_es: "Espagnol",
    lang_ja: "Japonais",
  },
  
  it: {
    nav_home: "Home",
    nav_mission: "Missione",
    nav_explanation: "Come funziona",
    nav_imprint: "Note legali",
    
    app_title: "WordRiver",
    app_subtitle: "Trascrizione Voce-a-Testo in Tempo Reale",
    
    btn_start: "Avvia",
    btn_pause: "Pausa",
    btn_stop: "Ferma",
    btn_resume: "Riprendi",
    btn_copy: "Copia testo",
    btn_export_txt: "Esporta come .txt",
    btn_export_rtf: "Esporta come .rtf",
    btn_clear: "Cancella",
    
    settings_title: "Impostazioni",
    settings_language: "Lingua di riconoscimento",
    settings_ui_language: "Lingua dell'interfaccia",
    settings_microphone: "Microfono",
    settings_font_size: "Dimensione testo",
    settings_line_spacing: "Interlinea",
    settings_text_fields: "Numero di campi di testo",
    settings_theme: "Tema",
    settings_theme_light: "Chiaro",
    settings_theme_dark: "Scuro",
    
    status_ready: "Pronto per registrare",
    status_listening: "In ascolto...",
    status_paused: "In pausa",
    status_stopped: "Fermato",
    status_not_supported: "Riconoscimento vocale non supportato in questo browser",
    status_permission_denied: "Permesso microfono negato",
    status_permission_hint: "Clicca su Avvia e consenti l'accesso al microfono",
    
    transcript_placeholder: "Il tuo testo trascritto apparirà qui...",
    transcript_empty: "Nessuna trascrizione ancora. Clicca 'Avvia' per iniziare.",
    
    toast_copied: "Testo copiato negli appunti",
    toast_exported: "File esportato con successo",
    toast_error: "Si è verificato un errore",
    toast_mic_changed: "Microfono cambiato",
    
    // Auto-save
    autosave_label: "Salvato automaticamente",
    autosave_just_now: "Proprio ora",
    
    // Keyboard shortcuts
    keyboard_shortcuts: "Scorciatoie da tastiera",
    keyboard_space: "Spazio: Avvia/Pausa",
    keyboard_escape: "Escape: Ferma",
    keyboard_copy: "Ctrl+Maiusc+C: Copia",
    
    footer_support: "Supporta questo progetto",
    footer_donate: "Dona",
    footer_privacy: "Privacy",
    footer_copyright: "© 2025 WordRiver. Tutti i diritti riservati.",
    
    mission_title: "La Nostra Missione",
    mission_subtitle: "Rendere la comunicazione accessibile a tutti",
    mission_p1: "WordRiver è stato sviluppato per abbattere le barriere comunicative. Crediamo che tutti meritino un accesso equo alle informazioni parlate, sia in classe, nelle riunioni o nella vita quotidiana.",
    mission_p2: "La nostra app offre trascrizione voce-testo in tempo reale ed è progettata per aiutare le persone a comprendere conversazioni, discussioni o presentazioni.",
    mission_p3: "Utilizzando tecnologia gratuita basata su browser, questo strumento è destinato ad essere accessibile a chiunque abbia accesso a Internet.",
    
    explanation_title: "Come funziona",
    explanation_subtitle: "Semplice, Gratuito",
    explanation_step1_title: "1. Seleziona la lingua",
    explanation_step1_desc: "Scegli la lingua parlata. Supportiamo tedesco, inglese, francese, italiano, spagnolo e giapponese.",
    explanation_step2_title: "2. Scegli il microfono",
    explanation_step2_desc: "Seleziona quale microfono usare. Puoi anche regolare la dimensione del testo e l'interlinea nelle impostazioni.",
    explanation_step3_title: "3. Inizia a registrare",
    explanation_step3_desc: "Clicca 'Avvia' e parla. Le tue parole appariranno come testo.",
    explanation_step4_title: "4. Esporta o copia",
    explanation_step4_desc: "Copia il testo o esportalo come file .txt o .rtf. Usa il pulsante Cancella per eliminare la trascrizione e ricominciare.",
    explanation_tech_title: "Tecnologia",
    explanation_tech_desc: "Utilizziamo l'API Web Speech, una tecnologia browser gratuita.",
    
    // Browser Compatibility
    browser_compatibility_title: "Compatibilità del browser",
    browser_compatibility_desc: "Questa applicazione utilizza l'API Web Speech per il riconoscimento vocale. Utilizza uno dei browser supportati:",
    browser_supported: "Supportato",
    browser_not_supported: "Non supportato",
    browser_recommendation: "Raccomandazione: Per la migliore esperienza, consigliamo Google Chrome.",
    browser_check_supported: "Browser compatibile",
    browser_check_supported_desc: "Il tuo browser ({browser}) supporta il riconoscimento vocale. Puoi utilizzare tutte le funzionalità.",
    browser_check_not_supported: "Browser non compatibile",
    browser_check_not_supported_desc: "Il tuo browser non supporta il riconoscimento vocale.",
    browser_check_firefox_msg: "Firefox non supporta l'API Web Speech per il riconoscimento vocale.",
    browser_check_recommendation: "Utilizza Chrome, Edge, Safari o Opera invece.",
    
    imprint_title: "Note legali",
    imprint_legal: "Informazioni legali",
    imprint_legal_text: "Questa è un'applicazione web gratuita per la trascrizione voce-testo in tempo reale. L'applicazione utilizza l'API Web Speech fornita dal tuo browser.",
    imprint_contact: "Contatto",
    imprint_contact_text: "Pedagogia sociale all'Università di Graz, Austria",
    imprint_disclaimer: "Disclaimer",
    imprint_disclaimer_text: "Questa applicazione utilizza la tecnologia di riconoscimento vocale del browser. La precisione può variare a seconda della qualità audio, dell'accento e dei fattori ambientali. Questo strumento viene fornito così com'è senza garanzia.",
    imprint_important_notice: "Avviso importante",
    imprint_privacy_data: "Trattamento dei dati",
    imprint_privacy_data_text: "Tutto il riconoscimento vocale viene elaborato localmente nel tuo browser utilizzando l'API Web Speech. Non inviamo i tuoi dati audio o di trascrizione a server esterni.",
    imprint_privacy_storage: "Archiviazione locale",
    imprint_privacy_storage_text: "Le tue impostazioni (preferenze di lingua, tema, selezione del microfono) sono memorizzate localmente nel tuo browser e non vengono trasmesse a nessun server.",
    imprint_privacy_donations: "Donazioni",
    imprint_privacy_donations_text: "Se scegli di donare, verrai reindirizzato alla pagina di pagamento sicura di Stripe. Non abbiamo accesso alle tue informazioni di pagamento.",
    
    lang_de: "Tedesco",
    lang_en: "Inglese",
    lang_fr: "Francese",
    lang_it: "Italiano",
    lang_es: "Spagnolo",
    lang_ja: "Giapponese",
  },
  
  es: {
    nav_home: "Inicio",
    nav_mission: "Misión",
    nav_explanation: "Cómo funciona",
    nav_imprint: "Aviso legal",
    
    app_title: "WordRiver",
    app_subtitle: "Transcripción de Voz a Texto en Tiempo Real",
    
    btn_start: "Iniciar",
    btn_pause: "Pausar",
    btn_stop: "Detener",
    btn_resume: "Reanudar",
    btn_copy: "Copiar texto",
    btn_export_txt: "Exportar como .txt",
    btn_export_rtf: "Exportar como .rtf",
    btn_clear: "Limpiar",
    
    settings_title: "Configuración",
    settings_language: "Idioma de reconocimiento",
    settings_ui_language: "Idioma de la interfaz",
    settings_microphone: "Micrófono",
    settings_font_size: "Tamaño de texto",
    settings_line_spacing: "Interlineado",
    settings_text_fields: "Número de campos de texto",
    settings_theme: "Tema",
    settings_theme_light: "Claro",
    settings_theme_dark: "Oscuro",
    
    status_ready: "Listo para grabar",
    status_listening: "Escuchando...",
    status_paused: "En pausa",
    status_stopped: "Detenido",
    status_not_supported: "Reconocimiento de voz no soportado en este navegador",
    status_permission_denied: "Permiso de micrófono denegado",
    status_permission_hint: "Haz clic en Iniciar y permite el acceso al micrófono",
    
    transcript_placeholder: "Tu texto transcrito aparecerá aquí...",
    transcript_empty: "Sin transcripción aún. Haz clic en 'Iniciar' para comenzar.",
    
    toast_copied: "Texto copiado al portapapeles",
    toast_exported: "Archivo exportado con éxito",
    toast_error: "Ocurrió un error",
    toast_mic_changed: "Micrófono cambiado",
    
    // Auto-save
    autosave_label: "Guardado automático",
    autosave_just_now: "Ahora mismo",
    
    // Keyboard shortcuts
    keyboard_shortcuts: "Atajos de teclado",
    keyboard_space: "Espacio: Iniciar/Pausar",
    keyboard_escape: "Escape: Detener",
    keyboard_copy: "Ctrl+Mayús+C: Copiar",
    
    footer_support: "Apoya este proyecto",
    footer_donate: "Donar",
    footer_privacy: "Privacidad",
    footer_copyright: "© 2025 WordRiver. Todos los derechos reservados.",
    
    mission_title: "Nuestra Misión",
    mission_subtitle: "Hacer la comunicación accesible para todos",
    mission_p1: "WordRiver fue desarrollado para derribar barreras de comunicación. Creemos que todos merecen igual acceso a la información hablada, ya sea en el aula, en reuniones o en la vida cotidiana.",
    mission_p2: "Nuestra app ofrece transcripción de voz a texto en tiempo real y está diseñada para ayudar a las personas a entender conversaciones, discusiones o presentaciones.",
    mission_p3: "Al usar tecnología gratuita basada en navegador, esta herramienta está destinada a ser accesible para cualquier persona con acceso a Internet.",
    
    explanation_title: "Cómo funciona",
    explanation_subtitle: "Simple, Gratuito",
    explanation_step1_title: "1. Selecciona tu idioma",
    explanation_step1_desc: "Elige el idioma hablado. Soportamos alemán, inglés, francés, italiano, español y japonés.",
    explanation_step2_title: "2. Elige tu micrófono",
    explanation_step2_desc: "Selecciona qué micrófono usar. También puedes ajustar el tamaño del texto y el interlineado en la configuración.",
    explanation_step3_title: "3. Comienza a grabar",
    explanation_step3_desc: "Haz clic en 'Iniciar' y habla. Tus palabras aparecerán como texto.",
    explanation_step4_title: "4. Exporta o copia",
    explanation_step4_desc: "Copia el texto o expórtalo como archivo .txt o .rtf. Usa el botón Limpiar para borrar la transcripción y empezar de nuevo.",
    explanation_tech_title: "Tecnología",
    explanation_tech_desc: "Usamos la API Web Speech, una tecnología de navegador gratuita.",
    
    // Browser Compatibility
    browser_compatibility_title: "Compatibilidad del navegador",
    browser_compatibility_desc: "Esta aplicación utiliza la API Web Speech para el reconocimiento de voz. Por favor, utiliza uno de los navegadores compatibles:",
    browser_supported: "Compatible",
    browser_not_supported: "No compatible",
    browser_recommendation: "Recomendación: Para la mejor experiencia, recomendamos Google Chrome.",
    browser_check_supported: "Navegador compatible",
    browser_check_supported_desc: "Tu navegador ({browser}) soporta el reconocimiento de voz. Puedes usar todas las funciones.",
    browser_check_not_supported: "Navegador no compatible",
    browser_check_not_supported_desc: "Tu navegador no soporta el reconocimiento de voz.",
    browser_check_firefox_msg: "Firefox no soporta la API Web Speech para el reconocimiento de voz.",
    browser_check_recommendation: "Por favor, usa Chrome, Edge, Safari u Opera en su lugar.",
    
    imprint_title: "Aviso legal",
    imprint_legal: "Información legal",
    imprint_legal_text: "Esta es una aplicación web gratuita para transcripción de voz a texto en tiempo real. La aplicación utiliza la API Web Speech proporcionada por tu navegador.",
    imprint_contact: "Contacto",
    imprint_contact_text: "Pedagogía social en la Universidad de Graz, Austria",
    imprint_disclaimer: "Descargo de responsabilidad",
    imprint_disclaimer_text: "Esta aplicación usa tecnología de reconocimiento de voz del navegador. La precisión puede variar según la calidad del audio, el acento y los factores ambientales. Esta herramienta se proporciona tal cual sin garantía.",
    imprint_important_notice: "Aviso importante",
    imprint_privacy_data: "Procesamiento de datos",
    imprint_privacy_data_text: "Todo el reconocimiento de voz se procesa localmente en tu navegador usando la API Web Speech. No enviamos tus datos de audio o transcripción a servidores externos.",
    imprint_privacy_storage: "Almacenamiento local",
    imprint_privacy_storage_text: "Tus configuraciones (preferencias de idioma, tema, selección de micrófono) se almacenan localmente en tu navegador y no se transmiten a ningún servidor.",
    imprint_privacy_donations: "Donaciones",
    imprint_privacy_donations_text: "Si eliges donar, serás redirigido a la página de pago segura de Stripe. No tenemos acceso a tu información de pago.",
    
    lang_de: "Alemán",
    lang_en: "Inglés",
    lang_fr: "Francés",
    lang_it: "Italiano",
    lang_es: "Español",
    lang_ja: "Japonés",
  },
};

// Language codes for Web Speech API
export const speechLanguageCodes = {
  de: "de-DE",
  en: "en-US",
  fr: "fr-FR",
  it: "it-IT",
  es: "es-ES",
  ja: "ja-JP",
};

// Available UI languages
export const availableLanguages = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
  { code: "es", name: "Español", flag: "🇪🇸" },
];

// Recognition languages (includes Japanese)
export const recognitionLanguages = [
  { code: "en", name: "English", speechCode: "en-US" },
  { code: "de", name: "Deutsch", speechCode: "de-DE" },
  { code: "fr", name: "Français", speechCode: "fr-FR" },
  { code: "it", name: "Italiano", speechCode: "it-IT" },
  { code: "es", name: "Español", speechCode: "es-ES" },
  { code: "ja", name: "日本語", speechCode: "ja-JP" },
];
